import { Checkbox } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import union from 'lodash/union';
import without from 'lodash/without';
import { PureComponent } from 'react';
import { change, WrappedFieldProps as WrappedFieldPropsT } from 'redux-form';

import styles from './categorizedMultiSelectCategory.module.scss';
import { CategorizedMultiSelectCategoryItem } from './categorizedMultiSelectCategoryItem';
import type {
  FieldCategorizedMultiSelectCategoryValuesItemT,
  FieldCategorizedMultiSelectCategoryValuesT,
} from './fieldCategorizedMultiSelect';

type PropsT = {
  category: FieldCategorizedMultiSelectCategoryValuesT;
  readOnly: boolean;
} & WrappedFieldPropsT;

export class CategorizedMultiSelectCategory extends PureComponent<PropsT> {
  getPossibleValues() {
    return this.props.category.items.map((item) => item.value);
  }

  getCheckedCategoryValues() {
    const pValues = this.getPossibleValues();
    return this.props.input.value.filter(
      (value: FieldCategorizedMultiSelectCategoryValuesItemT['value']) =>
        pValues.indexOf(value) !== -1,
    );
  }

  isIndeterminate() {
    const values = this.getCheckedCategoryValues();
    const pValues = this.getPossibleValues();
    return values.length > 0 && values.length < pValues.length;
  }

  isChecked() {
    const values = this.getCheckedCategoryValues();
    const pValues = this.getPossibleValues();
    return values.length === pValues.length;
  }

  @boundMethod
  selectCategory() {
    const { meta, input, readOnly } = this.props;
    if (readOnly) return;
    const { form: formName, dispatch } = meta;
    const { name, value } = input;

    const shouldUncheck = this.getCheckedCategoryValues().length > 0;
    const newValue = shouldUncheck
      ? without(value, ...this.getPossibleValues())
      : union(value, this.getPossibleValues());

    dispatch(change(formName, name, newValue));
  }

  render() {
    const { category, readOnly, ...otherProps } = this.props;

    const rootClass = classnames({
      [styles.root]: styles.root,
      [styles.rootDisabled]: category.disabled,
      [styles.rootReadOnly]: readOnly,
    });

    return (
      <div className={rootClass}>
        <label className={styles.name}>
          <Checkbox
            checked={this.isChecked()}
            name={category.categoryName}
            id={category.categoryName}
            indeterminate={this.isIndeterminate()}
            onChange={this.selectCategory}
            className={styles.checkbox}
            disabled={category.disabled}
          />
          {category.categoryName}
        </label>
        <div>
          {category.items.map((item) => (
            <CategorizedMultiSelectCategoryItem
              key={item.name}
              readOnly={readOnly}
              {...otherProps}
              item={item}
              disabled={category.disabled}
            />
          ))}
        </div>
      </div>
    );
  }
}
